import type { Rect } from './flex_label.ts';

/**
 * The legacy `FlowView` (Skia): a band of chevrons that moves along the longer side of its box,
 * or a grey bar when nothing flows. The legacy view advanced by `speed` device pixels per 100 ms;
 * the offset here is kept in CSS pixels, so the speed is divided by the pixel ratio.
 */

export interface Flow {
    /** Device pixels per 100 ms, as the legacy `Speed`. */
    speed: number;
    /** -1, 0 or 1; multiplied by `orientation`. 0: no flow. */
    direction: number;
    orientation: number;
    color: string;
    noFlowColor?: string;
}

/** The legacy default: `Color.FromRgb(0.2, 0.2, 0.2)`. */
export const NO_FLOW_COLOR = '#333333';

type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Where a flow's chevrons are, kept between paints. */
export interface FlowState {
    offset: number;
    /** When the flow was last painted (ms, any monotonic clock). */
    lastPaint?: number;
}

export function isFlowing(flow: Flow) : boolean {
    return flow.speed >= 0.01 && Math.sign(flow.direction * flow.orientation) !== 0;
}

/**
 * Paints the flow at time `now`, first moving the chevrons on by the time since the last paint,
 * as the legacy view did on each of its 33 ms timer ticks.
 */
export function drawFlow(ctx: Context, box: Rect, flow: Flow, state: FlowState, now: number) : void {
    const period = Math.min(box.width, box.height);
    if (state.lastPaint !== undefined && period > 0) {
        const pixelRatio = ctx.getTransform().a > 0 ? ctx.getTransform().a : 1;
        state.offset = (state.offset + ((now - state.lastPaint) / 100) * (flow.speed / pixelRatio)) % period;
    }
    state.lastPaint = now;

    if (!isFlowing(flow)) {
        ctx.fillStyle = flow.noFlowColor ?? NO_FLOW_COLOR;
        ctx.fillRect(box.left, box.top, box.width, box.height);

        return;
    }

    const isVertical = box.width < box.height;
    const length = isVertical ? box.height : box.width;
    const arrowWidth = Math.min(box.width, box.height);
    const spacing = arrowWidth / 2;
    const direction = Math.sign(flow.direction * flow.orientation);
    let offset = state.offset - spacing * 2;
    if (direction < 0) {
        offset = -offset;
    }

    ctx.save();
    // The legacy view drew on its own surface, so chevrons past the ends were cut off.
    ctx.beginPath();
    ctx.rect(box.left, box.top, box.width, box.height);
    ctx.clip();
    ctx.translate(box.left, box.top);
    ctx.fillStyle = flow.color;
    ctx.beginPath();
    const count = Math.floor(length / spacing / 2);
    for (let i = 0; i <= count + 1; i++) {
        const near = offset;
        const middle = offset + spacing * direction;
        const far = offset + spacing * 2 * direction;
        if (isVertical) {
            ctx.moveTo(0, near);
            ctx.lineTo(arrowWidth / 2, middle);
            ctx.lineTo(arrowWidth, near);
            ctx.lineTo(arrowWidth, middle);
            ctx.lineTo(arrowWidth / 2, far);
            ctx.lineTo(0, middle);
        } else {
            ctx.moveTo(near, 0);
            ctx.lineTo(middle, arrowWidth / 2);
            ctx.lineTo(near, arrowWidth);
            ctx.lineTo(middle, arrowWidth);
            ctx.lineTo(far, arrowWidth / 2);
            ctx.lineTo(middle, 0);
        }
        ctx.closePath();
        offset += spacing * 2;
    }
    ctx.fill();
    ctx.restore();
}
